import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BaseLogo, LogoType } from "../core/models/entities/BaseLogo";
import { actAsCacheKey } from "../contexts/actAsStore";
import { enviroment } from "../enviroment";
import { DEFAULT_HEAL_DELAYS_MS } from "../utils/functions/healDelays";

// The logo system's live render returns an empty base64 payload for a
// seconds-old serial; the API always prefixes it, so a "blank" image reaches
// the client as the non-loadable string "data:image/png;base64, " (note the
// empty payload). Browsers render that as the broken-image glyph.
const BLANK_DATA_URI = /^data:image\/[a-z]+;base64,\s*$/;

export function isBlankDataUri(url: string | undefined): boolean {
  return !!url && BLANK_DATA_URI.test(url);
}

// A freshly uploaded/cloned ImageLogo has no genuine high-res render yet:
// either the server sent no highResUrl (the mapper falls back to lowResUrl),
// or GetLogo live-rendered a still-blank serial (identical blank data-URIs
// for both resolutions). Both states are TEMPORARY for ImageLogos - the logo
// system finishes the serial seconds-to-minutes later - but the cached
// GetLogo response never refetches on its own (refetchOnMount is off
// globally), so without help the blur/placeholder sticks until F5.
// awaitingImage is excluded: that path is owned by the image-recovery worker
// and GetLogo deliberately skips the live render there.
export function isHighResPending(logo: BaseLogo | undefined): boolean {
  if (!logo) return false;
  if (logo.logoType !== LogoType.ImageLogo) return false;
  if (logo.awaitingImage) return false;
  const image = logo.displayImage;
  if (!image?.lowResUrl) return false;
  // Flag REACT_APP_LOGO_HEAL_PER_MUTATION: honour an EXPLICIT pending mark.
  // The copy response carries the 180px processed blob as highResUrl (a
  // data-URI that differs from lowResUrl), so the equality rule below never
  // classifies a clone as pending; the clone flow marks the seeded entry
  // instead (markDetailHighResPending). For mapped GetLogo data the field
  // equals "no highResUrl from the server", i.e. the same as the equality
  // rule, so nothing else changes.
  if (enviroment.logoHealPerMutationEnabled && image.highResPending === true) {
    return true;
  }
  return (
    !image.highResUrl ||
    image.highResUrl === image.lowResUrl ||
    isBlankDataUri(image.highResUrl)
  );
}

// Backoff schedule for the bounded heal: at most 3 extra GetLogo calls per
// mount, spread so a fast logo system heals in ~5s and a slow one within ~65s.
// With REACT_APP_LOGO_HEAL_PER_MUTATION the schedule can be tuned via
// REACT_APP_HEAL_DELAYS_MS (parsed and capped to 3 in enviroment.ts); flag
// off = the historical constant.
export function getHealDelaysMs(): readonly number[] {
  return enviroment.logoHealPerMutationEnabled
    ? enviroment.healDelaysMs
    : DEFAULT_HEAL_DELAYS_MS;
}

/**
 * Bounded self-heal for ImageLogos whose real high-res render doesn't exist
 * yet (fresh upload or clone). While `detailLogo` still carries a pending
 * high-res, schedules up to three background refetches of the detail entry
 * (["logos", actAs, logoId])
 * (5s / 15s / 45s), then stops. Same invalidate pattern as the clone heal
 * (#565): refetchType "all" bypasses both staleTime and the global
 * refetchOnMount:false. Stops immediately when a genuine high-res arrives
 * (pending flips false), and cleans up its timer on unmount - never an
 * endless poll. Pass the DETAIL query's data (not a list stub), so logos
 * whose detail already has a real high-res are never polled.
 *
 * `resetToken` (optional, flag REACT_APP_LOGO_HEAL_PER_MUTATION): every
 * change of the token restarts the poll budget - the editor bumps it after
 * each mutation (upload / colour / resize / draft save), so a mutation on an
 * already-healed logo gets its own 3 polls instead of none. Two mutations in
 * a row do NOT add up: the later bump restarts from attempt 0, so the budget
 * is always "max 3 after the LAST mutation". Callers that never pass the
 * token keep the exact historical behaviour (reset only on logoId change).
 */
export function useHealPendingHighRes(
  logoId: BaseLogo["id"] | undefined,
  detailLogo: BaseLogo | undefined,
  resetToken?: number
): void {
  const queryClient = useQueryClient();
  const attempt = useRef(0);

  useEffect(() => {
    attempt.current = 0;
  }, [logoId, resetToken]);

  const pending = isHighResPending(detailLogo);

  useEffect(() => {
    if (!logoId || !pending) return;
    const delays = getHealDelaysMs();
    if (attempt.current >= delays.length) return;
    const timer = setTimeout(() => {
      attempt.current += 1;
      // Detail keys carry the act-as segment (["logos", actAs, id]) — target
      // the CURRENT scope's entry, i.e. the one the popup's useLogo is
      // reading. Same shared helper as LogoRepository's keys.
      void queryClient.invalidateQueries({
        queryKey: ["logos", actAsCacheKey(), logoId],
        refetchType: "all",
      });
    }, delays[attempt.current]);
    return () => clearTimeout(timer);
    // detailLogo in deps: every refetch produces a new data identity (mapped
    // Dates defeat structural sharing), which re-runs this effect and either
    // schedules the next attempt or - once pending is false - stops.
    // resetToken in deps: a bump must re-arm the timer even when the data
    // identity did not change (e.g. the mutation response equals the cached
    // entry), otherwise the reset above would be invisible to this effect.
  }, [pending, detailLogo, logoId, resetToken, queryClient]);
}
